<div class="container-fluid py-3">
  <!-- Header -->
  <div class="d-flex justify-content-between align-items-center mb-4">
    <h4 class="mb-0"><i class="bi bi-plus-circle me-2"></i>{{ 'SystemAdmin.newCustomer.title' | translate }}</h4>
    <button type="button" class="btn btn-outline-secondary btn-sm" (click)="goToDomainManagement()">
      <i class="bi bi-arrow-left me-1"></i>{{ 'SystemAdmin.newCustomer.backToDomains' | translate }}
    </button>
  </div>

  <!-- Stepper indicator -->
  <div class="stepper-container mb-4" *ngIf="!provisioningResult">
    <div class="stepper d-flex justify-content-between">
      <div *ngFor="let label of stepLabels; let i = index"
           class="step text-center"
           [class.active]="currentStep === (i + 1)"
           [class.completed]="currentStep > (i + 1)"
           (click)="goToStep(i + 1)"
           [style.cursor]="(i + 1) <= currentStep ? 'pointer' : 'default'">
        <div class="step-circle" [class.bg-primary]="currentStep >= (i + 1)" [class.bg-light]="currentStep < (i + 1)">
          <span *ngIf="currentStep <= (i + 1)">{{ i + 1 }}</span>
          <i *ngIf="currentStep > (i + 1)" class="bi bi-check-lg"></i>
        </div>
        <small class="step-label d-none d-md-block mt-1">{{ label }}</small>
      </div>
    </div>
  </div>

  <!-- ════════════════ Step 1: Customer ════════════════ -->
  <div *ngIf="currentStep === 1 && !provisioningResult">
    <fieldset class="form-section">
      <legend class="form-section-legend">{{ 'SystemAdmin.newCustomer.customerInfo' | translate }}</legend>

      <div class="mb-3">
        <div class="form-check form-switch">
          <input class="form-check-input" type="checkbox" id="useExisting"
                 [checked]="customerForm.get('useExisting')?.value"
                 (change)="customerForm.patchValue({useExisting: $any($event.target).checked}); onCustomerTypeChange()">
          <label class="form-check-label" for="useExisting">
            {{ 'SystemAdmin.newCustomer.useExistingCustomer' | translate }}
          </label>
        </div>
      </div>

      <!-- Existing customer dropdown -->
      <div *ngIf="useExistingCustomer" class="row g-3" [formGroup]="customerForm">
        <div class="col-md-6">
          <label class="form-label">{{ 'SystemAdmin.newCustomer.selectCustomer' | translate }}</label>
          <select class="form-select" formControlName="existingUserAccountID">
            <option [ngValue]="null">{{ 'SystemAdmin.newCustomer.selectPlaceholder' | translate }}</option>
            <option *ngFor="let acc of userAccounts" [ngValue]="acc.userAccountID">{{ acc.fullName }}</option>
          </select>
        </div>
      </div>

      <!-- New customer form -->
      <div *ngIf="!useExistingCustomer" [formGroup]="customerForm">
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label">{{ 'SystemAdmin.newCustomer.customerName' | translate }} *</label>
            <input type="text" class="form-control" formControlName="newCustomerName"
                   [class.is-invalid]="customerForm.get('newCustomerName')?.touched && customerForm.hasError('customerRequired')">
          </div>
          <div class="col-md-6">
            <label class="form-label">{{ 'SystemAdmin.newCustomer.email' | translate }}</label>
            <input type="email" class="form-control" formControlName="newCustomerEmail">
          </div>
          <div class="col-md-4">
            <label class="form-label">{{ 'SystemAdmin.newCustomer.address' | translate }}</label>
            <input type="text" class="form-control" formControlName="newCustomerAddress">
          </div>
          <div class="col-md-2">
            <label class="form-label">{{ 'SystemAdmin.newCustomer.zip' | translate }}</label>
            <input type="text" class="form-control" formControlName="newCustomerZip">
          </div>
          <div class="col-md-3">
            <label class="form-label">{{ 'SystemAdmin.newCustomer.city' | translate }}</label>
            <input type="text" class="form-control" formControlName="newCustomerCity">
          </div>
          <div class="col-md-3">
            <label class="form-label">{{ 'SystemAdmin.newCustomer.phone' | translate }}</label>
            <input type="text" class="form-control" formControlName="newCustomerPhone">
          </div>
        </div>
      </div>
    </fieldset>
  </div>

  <!-- ════════════════ Step 2: Domain ════════════════ -->
  <div *ngIf="currentStep === 2 && !provisioningResult">
    <fieldset class="form-section">
      <legend class="form-section-legend">{{ 'SystemAdmin.newCustomer.domainConfig' | translate }}</legend>

      <div class="row g-3" [formGroup]="domainForm">
        <div class="col-md-6">
          <label class="form-label">{{ 'SystemAdmin.newCustomer.domainName' | translate }} *</label>
          <div class="input-group">
            <input type="text" class="form-control" formControlName="domainName"
                   (input)="onDomainNameChange()" (blur)="onDomainNameBlur()"
                   [class.is-invalid]="domainForm.get('domainName')?.touched && domainForm.get('domainName')?.invalid"
                   [class.is-valid]="domainNameAvailable === true"
                   [placeholder]="'SystemAdmin.newCustomer.domainPlaceholder' | translate">
            <span class="input-group-text">.eportal.no</span>
          </div>
          <div class="form-text" *ngIf="domainNameChecking">
            <i class="bi bi-arrow-clockwise spin"></i> {{ 'SystemAdmin.newCustomer.checking' | translate }}
          </div>
          <div class="text-success small mt-1" *ngIf="domainNameAvailable === true && !domainNameChecking">
            <i class="bi bi-check-circle"></i> {{ 'SystemAdmin.newCustomer.nameAvailable' | translate }}
          </div>
          <div class="text-danger small mt-1" *ngIf="domainNameAvailable === false && !domainNameChecking">
            <i class="bi bi-x-circle"></i> {{ 'SystemAdmin.newCustomer.nameTaken' | translate }}
          </div>
        </div>
        <div class="col-md-6">
          <label class="form-label">{{ 'SystemAdmin.newCustomer.domainColors' | translate }}</label>
          <input type="text" class="form-control" formControlName="domainColors"
                 [placeholder]="'SystemAdmin.newCustomer.colorsPlaceholder' | translate">
        </div>
        <div class="col-md-4">
          <label class="form-label">{{ 'SystemAdmin.newCustomer.logo' | translate }}</label>
          <input type="text" class="form-control" formControlName="userLogo"
                 [placeholder]="'SystemAdmin.newCustomer.logoPlaceholder' | translate">
        </div>
        <div class="col-md-4">
          <label class="form-label">{{ 'SystemAdmin.newCustomer.logoSmall' | translate }}</label>
          <input type="text" class="form-control" formControlName="userLogoSmall">
        </div>
        <div class="col-md-4">
          <label class="form-label">{{ 'SystemAdmin.newCustomer.background' | translate }}</label>
          <input type="text" class="form-control" formControlName="userBackground">
        </div>
      </div>
    </fieldset>
  </div>

  <!-- ════════════════ Step 3: Database ════════════════ -->
  <div *ngIf="currentStep === 3 && !provisioningResult">
    <fieldset class="form-section">
      <legend class="form-section-legend">{{ 'SystemAdmin.newCustomer.databaseConfig' | translate }}</legend>

      <div class="row g-3" [formGroup]="databaseForm">
        <div class="col-md-6">
          <label class="form-label">{{ 'SystemAdmin.newCustomer.databaseName' | translate }} *</label>
          <input type="text" class="form-control" formControlName="databaseName"
                 (input)="onDbNameChange()"
                 [class.is-invalid]="databaseForm.get('databaseName')?.touched && databaseForm.get('databaseName')?.invalid"
                 [class.is-valid]="dbNameAvailable === true">
          <div class="form-text">{{ 'SystemAdmin.newCustomer.dbNameHelp' | translate }}</div>
          <div class="form-text" *ngIf="dbNameChecking">
            <i class="bi bi-arrow-clockwise spin"></i> {{ 'SystemAdmin.newCustomer.checking' | translate }}
          </div>
          <div class="text-success small mt-1" *ngIf="dbNameAvailable === true && !dbNameChecking">
            <i class="bi bi-check-circle"></i> {{ 'SystemAdmin.newCustomer.nameAvailable' | translate }}
          </div>
          <div class="text-danger small mt-1" *ngIf="dbNameAvailable === false && !dbNameChecking">
            <i class="bi bi-x-circle"></i> {{ 'SystemAdmin.newCustomer.nameTaken' | translate }}
          </div>
        </div>
        <div class="col-md-6">
          <label class="form-label">{{ 'SystemAdmin.newCustomer.sqlServerType' | translate }} *</label>
          <select class="form-select" formControlName="sqlServerType">
            <option value="OnPremise">{{ 'SystemAdmin.newCustomer.onPremise' | translate }}</option>
            <option value="AzureSql">{{ 'SystemAdmin.newCustomer.azureSql' | translate }}</option>
          </select>
        </div>

        <!-- SQL Server connection (both OnPremise and AzureSql) -->
        <div class="col-md-12 mt-2">
          <hr class="my-2">
          <small class="text-muted">{{ 'SystemAdmin.newCustomer.sqlConnectionHelp' | translate }}</small>
        </div>
        <div class="col-md-6">
          <label class="form-label">{{ 'SystemAdmin.newCustomer.sqlServerAddress' | translate }}</label>
          <input type="text" class="form-control" formControlName="sqlServerAddress"
                 [placeholder]="'SystemAdmin.newCustomer.sqlServerAddressPlaceholder' | translate">
          <div class="form-text">{{ 'SystemAdmin.newCustomer.sqlServerAddressHelp' | translate }}</div>
        </div>
        <div class="col-md-3">
          <label class="form-label">{{ 'SystemAdmin.newCustomer.sqlAdminLogin' | translate }}</label>
          <input type="text" class="form-control" formControlName="sqlAdminLogin"
                 [placeholder]="'SystemAdmin.newCustomer.sqlAdminLoginPlaceholder' | translate">
        </div>
        <div class="col-md-3">
          <label class="form-label">{{ 'SystemAdmin.newCustomer.sqlAdminPassword' | translate }}</label>
          <input type="password" class="form-control" formControlName="sqlAdminPassword"
                 [placeholder]="'SystemAdmin.newCustomer.sqlAdminPasswordPlaceholder' | translate">
        </div>
      </div>
    </fieldset>
  </div>

  <!-- ════════════════ Step 4: Admin User ════════════════ -->
  <div *ngIf="currentStep === 4 && !provisioningResult">
    <fieldset class="form-section">
      <legend class="form-section-legend">{{ 'SystemAdmin.newCustomer.adminUser' | translate }}</legend>

      <div class="row g-3" [formGroup]="adminForm">
        <div class="col-md-6">
          <label class="form-label">{{ 'SystemAdmin.newCustomer.adminFullName' | translate }} *</label>
          <input type="text" class="form-control" formControlName="adminFullName"
                 [class.is-invalid]="adminForm.get('adminFullName')?.touched && adminForm.get('adminFullName')?.invalid">
        </div>
        <div class="col-md-6">
          <label class="form-label">{{ 'SystemAdmin.newCustomer.adminEmail' | translate }}</label>
          <input type="email" class="form-control" formControlName="adminEmail">
        </div>
        <div class="col-md-6">
          <label class="form-label">{{ 'SystemAdmin.newCustomer.adminUsername' | translate }} *</label>
          <input type="text" class="form-control" formControlName="adminUsername"
                 [class.is-invalid]="adminForm.get('adminUsername')?.touched && adminForm.get('adminUsername')?.invalid">
          <div class="form-text">{{ 'SystemAdmin.newCustomer.adminUsernameHelp' | translate }}</div>
        </div>
        <div class="col-md-3">
          <label class="form-label">{{ 'SystemAdmin.newCustomer.adminPassword' | translate }} *</label>
          <input type="password" class="form-control" formControlName="adminPassword"
                 [class.is-invalid]="adminForm.get('adminPassword')?.touched && adminForm.get('adminPassword')?.invalid">
          <div class="form-text">{{ 'SystemAdmin.newCustomer.adminPasswordHelp' | translate }}</div>
        </div>
        <div class="col-md-3">
          <label class="form-label">{{ 'SystemAdmin.newCustomer.confirmPassword' | translate }} *</label>
          <input type="password" class="form-control" formControlName="adminPasswordConfirm"
                 [class.is-invalid]="adminForm.hasError('passwordMismatch') && adminForm.get('adminPasswordConfirm')?.touched">
          <div class="invalid-feedback" *ngIf="adminForm.hasError('passwordMismatch')">
            {{ 'SystemAdmin.newCustomer.passwordMismatch' | translate }}
          </div>
        </div>
      </div>
    </fieldset>
  </div>

  <!-- ════════════════ Step 5: Modules ════════════════ -->
  <div *ngIf="currentStep === 5 && !provisioningResult">
    <fieldset class="form-section">
      <legend class="form-section-legend">{{ 'SystemAdmin.newCustomer.moduleSelection' | translate }}</legend>

      <!-- Feature flags -->
      <div class="row g-3 mb-4" [formGroup]="modulesForm">
        <div class="col-md-4">
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" id="useExternalWorkOrders" formControlName="useExternalWorkOrders">
            <label class="form-check-label" for="useExternalWorkOrders">
              {{ 'SystemAdmin.newCustomer.useExternalWorkOrders' | translate }}
            </label>
          </div>
        </div>
        <div class="col-md-4">
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" id="useStockIntegration" formControlName="useStockIntegration">
            <label class="form-check-label" for="useStockIntegration">
              {{ 'SystemAdmin.newCustomer.useStockIntegration' | translate }}
            </label>
          </div>
        </div>
        <div class="col-md-4">
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" id="useBugReport" formControlName="useBugReport">
            <label class="form-check-label" for="useBugReport">
              {{ 'SystemAdmin.newCustomer.useBugReport' | translate }}
            </label>
          </div>
        </div>
      </div>

      <!-- Module checkboxes -->
      <h6>{{ 'SystemAdmin.newCustomer.availableModules' | translate }}</h6>
      <div class="row g-2">
        <div class="col-md-4 col-lg-3" *ngFor="let mod of availableModules">
          <div class="card p-2 cursor-pointer" [class.border-primary]="isModuleSelected(mod.moduleID)"
               (click)="toggleModule(mod.moduleID)">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" [checked]="isModuleSelected(mod.moduleID)"
                     (change)="toggleModule(mod.moduleID)" (click)="$event.stopPropagation()">
              <label class="form-check-label">
                <strong>{{ mod.moduleName }}</strong>
                <div class="text-muted small" *ngIf="mod.moduleDescription">{{ mod.moduleDescription }}</div>
              </label>
            </div>
          </div>
        </div>
      </div>
    </fieldset>
  </div>

  <!-- ════════════════ Step 6: Review ════════════════ -->
  <div *ngIf="currentStep === 6 && !provisioningResult">
    <fieldset class="form-section">
      <legend class="form-section-legend">{{ 'SystemAdmin.newCustomer.reviewTitle' | translate }}</legend>

      <div class="row g-3">
        <div class="col-md-6">
          <div class="card p-3">
            <h6><i class="bi bi-person-badge me-1"></i>{{ 'SystemAdmin.newCustomer.stepCustomer' | translate }}</h6>
            <p class="mb-1">
              <strong>{{ 'SystemAdmin.newCustomer.customerName' | translate }}:</strong>
              {{ reviewData.customer.name }}
              <span class="badge badge-soft-info ms-1" *ngIf="reviewData.customer.isExisting">
                {{ 'SystemAdmin.newCustomer.existing' | translate }}
              </span>
            </p>
          </div>
        </div>
        <div class="col-md-6">
          <div class="card p-3">
            <h6><i class="bi bi-globe me-1"></i>{{ 'SystemAdmin.newCustomer.stepDomain' | translate }}</h6>
            <p class="mb-1"><strong>{{ 'SystemAdmin.newCustomer.domainName' | translate }}:</strong> {{ reviewData.domainName }}.eportal.no</p>
          </div>
        </div>
        <div class="col-md-6">
          <div class="card p-3">
            <h6><i class="bi bi-database me-1"></i>{{ 'SystemAdmin.newCustomer.stepDatabase' | translate }}</h6>
            <p class="mb-1"><strong>{{ 'SystemAdmin.newCustomer.databaseName' | translate }}:</strong> {{ reviewData.databaseName }}</p>
            <p class="mb-0"><strong>{{ 'SystemAdmin.newCustomer.sqlServerType' | translate }}:</strong> {{ reviewData.sqlServerType }}</p>
          </div>
        </div>
        <div class="col-md-6">
          <div class="card p-3">
            <h6><i class="bi bi-shield-lock me-1"></i>{{ 'SystemAdmin.newCustomer.stepAdmin' | translate }}</h6>
            <p class="mb-1"><strong>{{ 'SystemAdmin.newCustomer.adminUsername' | translate }}:</strong> {{ reviewData.adminUsername }}</p>
            <p class="mb-0"><strong>{{ 'SystemAdmin.newCustomer.adminFullName' | translate }}:</strong> {{ reviewData.adminFullName }}</p>
          </div>
        </div>
        <div class="col-12">
          <div class="card p-3">
            <h6><i class="bi bi-grid me-1"></i>{{ 'SystemAdmin.newCustomer.stepModules' | translate }}</h6>
            <p class="mb-0">{{ reviewData.moduleCount }} {{ 'SystemAdmin.newCustomer.modulesSelected' | translate }}</p>
          </div>
        </div>
      </div>

      <div class="alert alert-warning mt-3">
        <i class="bi bi-exclamation-triangle me-1"></i>
        {{ 'SystemAdmin.newCustomer.provisionWarning' | translate }}
      </div>
    </fieldset>
  </div>

  <!-- ════════════════ Provisioning Result ════════════════ -->
  <div *ngIf="provisioningResult">
    <fieldset class="form-section">
      <legend class="form-section-legend">
        <i class="bi" [ngClass]="provisioningResult.success ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'"></i>
        {{ (provisioningResult.success ? 'SystemAdmin.newCustomer.provisionSuccessTitle' : 'SystemAdmin.newCustomer.provisionFailedTitle') | translate }}
      </legend>

      <!-- Success summary -->
      <div *ngIf="provisioningResult.success" class="row g-3 mb-3">
        <div class="col-md-6">
          <table class="table table-sm">
            <tr>
              <td class="text-muted">{{ 'SystemAdmin.newCustomer.domainName' | translate }}</td>
              <td><strong>{{ provisioningResult.domainName }}</strong></td>
            </tr>
            <tr>
              <td class="text-muted">{{ 'SystemAdmin.domainID' | translate }}</td>
              <td>{{ provisioningResult.domainID }}</td>
            </tr>
            <tr>
              <td class="text-muted">{{ 'SystemAdmin.newCustomer.databaseName' | translate }}</td>
              <td>{{ provisioningResult.databaseName }}</td>
            </tr>
            <tr>
              <td class="text-muted">{{ 'SystemAdmin.newCustomer.sqlLogin' | translate }}</td>
              <td>{{ provisioningResult.sqlLoginName }}</td>
            </tr>
            <tr *ngIf="provisioningResult.sqlPassword">
              <td class="text-muted">{{ 'SystemAdmin.newCustomer.sqlPassword' | translate }}</td>
              <td>
                <code class="small user-select-all">{{ showSqlPassword ? provisioningResult.sqlPassword : '••••••••••••' }}</code>
                <button type="button" class="btn btn-sm btn-outline-secondary ms-2 py-0 px-1" (click)="showSqlPassword = !showSqlPassword" [title]="(showSqlPassword ? 'General.Hide' : 'General.Show') | translate" [attr.aria-label]="(showSqlPassword ? 'General.Hide' : 'General.Show') | translate">
                  <i class="bi" [ngClass]="showSqlPassword ? 'bi-eye-slash' : 'bi-eye'"></i>
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary ms-1 py-0 px-1" (click)="copyToClipboard(provisioningResult.sqlPassword)" [title]="'General.copy' | translate" [attr.aria-label]="'General.copy' | translate">
                  <i class="bi bi-clipboard"></i>
                </button>
              </td>
            </tr>
            <tr>
              <td class="text-muted">{{ 'SystemAdmin.newCustomer.apiKey' | translate }}</td>
              <td>
                <code class="small">{{ showApiKey ? provisioningResult.apiKey : '••••••••••••••••' }}</code>
                <button type="button" class="btn btn-sm btn-outline-secondary ms-1 py-0 px-1" (click)="showApiKey = !showApiKey"
                        [attr.title]="(showApiKey ? 'General.hide' : 'General.show') | translate"
                        [attr.aria-label]="(showApiKey ? 'General.hide' : 'General.show') | translate">
                  <i class="bi" [ngClass]="showApiKey ? 'bi-eye-slash' : 'bi-eye'"></i>
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary ms-1 py-0 px-1" (click)="copyToClipboard(provisioningResult.apiKey)"
                        [attr.title]="'General.copy' | translate" [attr.aria-label]="'General.copy' | translate">
                  <i class="bi bi-clipboard"></i>
                </button>
              </td>
            </tr>
          </table>
        </div>
      </div>

      <div *ngIf="provisioningResult.sqlPassword" class="alert alert-warning mb-3">
        <i class="bi bi-exclamation-triangle me-1"></i>
        {{ 'SystemAdmin.newCustomer.sqlPasswordWarning' | translate }}
      </div>

      <!-- Steps log -->
      <h6>{{ 'SystemAdmin.newCustomer.provisioningLog' | translate }}</h6>
      <div class="steps-log">
        <div *ngFor="let step of provisioningResult.steps" class="step-log-entry small"
             [ngClass]="{'text-success': step.startsWith('✓'), 'text-danger': step.startsWith('✗'), 'text-muted': !step.startsWith('✓') && !step.startsWith('✗')}">
          {{ step }}
        </div>
      </div>

      <!-- Error details -->
      <div *ngIf="provisioningError" class="alert alert-danger mt-3">
        {{ provisioningError }}
      </div>
    </fieldset>

    <div class="d-flex gap-2 mt-3">
      <button *ngIf="!provisioningResult.success" type="button" class="btn btn-dark" (click)="goBackToReview()">
        <i class="bi bi-arrow-left me-1"></i>{{ 'General.Back' | translate }}
      </button>
      <button type="button" class="btn btn-primary" (click)="goToDomainManagement()">
        <i class="bi bi-arrow-left me-1"></i>{{ 'SystemAdmin.newCustomer.backToDomains' | translate }}
      </button>
      <button type="button" class="btn btn-outline-primary" (click)="startNewProvisioning()">
        <i class="bi bi-plus-circle me-1"></i>{{ 'SystemAdmin.newCustomer.provisionAnother' | translate }}
      </button>
    </div>
  </div>

  <!-- ════════════════ Navigation buttons ════════════════ -->
  <div class="d-flex justify-content-between mt-4" *ngIf="!provisioningResult">
    <button type="button" class="btn btn-outline-secondary" (click)="previousStep()" [disabled]="currentStep === 1">
      <i class="bi bi-arrow-left me-1"></i>{{ 'General.Back' | translate }}
    </button>
    <div>
      <button *ngIf="currentStep < totalSteps" type="button" class="btn btn-primary" (click)="nextStep()" [disabled]="!canGoNext()">
        {{ 'General.Next' | translate }} <i class="bi bi-arrow-right ms-1"></i>
      </button>
      <button *ngIf="currentStep === totalSteps" type="button" class="btn btn-success btn-lg" (click)="startProvisioning()" [disabled]="isProvisioning">
        <span *ngIf="!isProvisioning">
          <i class="bi bi-play-circle me-1"></i>{{ 'SystemAdmin.newCustomer.startProvisioning' | translate }}
        </span>
        <span *ngIf="isProvisioning">
          <span class="spinner-border spinner-border-sm me-1"></span>{{ 'SystemAdmin.newCustomer.provisioning' | translate }}
        </span>
      </button>
    </div>
  </div>
</div>
